import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  ARColzaCampaign,
  ARColzaCampaignConfiguration,
  ARColzaOrder,
  ARColzaOrderItem,
  ARColzaParcel,
} from '../models/agrorendement-colza.model';
import { Page } from '../models/page.model';
import { OrderItemDeletionSummary, normalizeDeletionSummary } from '../models/order-item-deletion.model';
import { Parcel } from '../models/parcel.model';
import { HttpWanaka } from '../utils/http.wanaka';
import { tryJson } from '../utils/utils';
import { AROrderFilter, ARResultService, OrderSummary, fillOrderFilter } from './agrorendement-common.service';

export class ARColzaParcelFilter {
  prefixParcel: string;
  prefixExploitation: string;
  farmId: number | string;
  campaignId: number | number[];
  organisationId: number | string | number[];
  orderStatus: boolean;
  completionStatus: boolean;
  prefixIdParcel: number;
  variety: number | string;
  advisorId: number | string;
}

@Injectable({ providedIn: 'root' })
export class ARColzaService extends ARResultService<ARColzaOrderItem, ARColzaCampaign> {
  protected toolUrl = '/tools/agrorendement/colza';

  constructor(
    protected http: HttpClient,
    protected httpWanaka: HttpWanaka,
  ) {
    super(http, httpWanaka);
  }

  canPerfomLifecycleUpdates(): Observable<boolean> {
    const url = `${this.toolUrl}/results/canManage`;
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  getPageCampaigns(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<ARColzaCampaign>> {
    let url = this.toolUrl + '/campaigns?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    if (filter) {
      url += '&year=' + encodeURIComponent(filter.trim());
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: ARColzaCampaign) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  updateParcelsBatch(parcelIds: number[], newDate: any, passage: string, isSuspended: any): Observable<any> {
    const payload: any = { parcelIds, passage };

    if (newDate) {
      payload.newDate = newDate;
    }

    if (isSuspended !== undefined && isSuspended !== null) {
      payload.isSuspended = isSuspended;
    }

    return this.http.put<any>(`${this.toolUrl}/orderitems/update-batch`, payload);
  }

  getCampaigns(): Observable<ARColzaCampaign[]> {
    return this.http.get<any>(this.toolUrl + '/campaigns/all?configurationStatus=true').pipe(
      map((response) => {
        const data: ARColzaCampaign[] = response.data;
        data.forEach((element) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaign(id: number): Observable<ARColzaCampaign> {
    return this.http.get<any>(this.toolUrl + '/campaigns/' + id).pipe(
      map((response) => {
        const data: ARColzaCampaign = response.data;
        this.handleCampaignDates(data);
        if (data.configuration) {
          this.handleConfigurationDates(data.configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfigurations(): Observable<ARColzaCampaignConfiguration[]> {
    return this.http.get<any>(this.toolUrl + '/configs/all').pipe(
      map((response) => {
        const data: ARColzaCampaignConfiguration[] = response.data;
        data.forEach((element: ARColzaCampaignConfiguration) => {
          this.handleConfigurationDates(element);
          if (element.campaign) {
            this.handleCampaignDates(element.campaign);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfiguration(id: number): Observable<ARColzaCampaignConfiguration> {
    return this.http.get<any>(this.toolUrl + '/configs/' + id).pipe(
      map((response) => {
        const data: ARColzaCampaignConfiguration = this.handleConfigurationDates(response.data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveCampaignConfiguration(config: ARColzaCampaignConfiguration): Observable<ARColzaCampaignConfiguration> {
    if (config.id) {
      return this.putCampaignConfiguration(config);
    }
    return this.postCampaignConfiguration(config);
  }

  deleteCampaignConfiguration(config: ARColzaCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  // Add new campaign
  private postCampaignConfiguration(config: ARColzaCampaignConfiguration): Observable<ARColzaCampaignConfiguration> {
    return this.http.post<any>(this.toolUrl + '/configs', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putCampaignConfiguration(config: ARColzaCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.put<any>(url, config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  getPageParcels(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: ARColzaParcelFilter,
  ): Observable<Page<ARColzaParcel>> {
    let url = this.toolUrl + '/parcels?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    url += this.buildParamsFromFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: ARColzaParcel) => {
          this.handleParcel(element);
          if (element.campaign) {
            this.handleCampaignDates(<ARColzaCampaign>element.campaign);
          }
          if ((<ARColzaCampaign>element.campaign).configuration) {
            this.handleConfigurationDates((<ARColzaCampaign>element.campaign).configuration);
          }
        });

        return data;
      }),
      catchError(this.handleError),
    );
  }

  getAllParcelIds(filter: ARColzaParcelFilter): Observable<number[]> {
    let url = this.toolUrl + '/parcels/all?onlyIds=true';
    url += this.buildParamsFromFilter(filter);
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        return data.map((x: ARColzaParcel) => x.id);
      }),
      catchError(this.handleError),
    );
  }

  buildParamsFromFilter(filter: ARColzaParcelFilter): string {
    let url = '';
    if (filter) {
      if (filter.prefixParcel) {
        url += '&prefixParcel=' + encodeURIComponent(filter.prefixParcel.trim());
      }
      if (filter.prefixExploitation) {
        url += '&prefixExploitation=' + encodeURIComponent(filter.prefixExploitation.trim());
      }
      if (filter.prefixIdParcel) {
        url += '&prefixIdParcel=' + filter.prefixIdParcel;
      }
      if (filter.farmId) {
        url += '&farm=' + filter.farmId;
      }
      if (filter.organisationId) {
        url += '&organisation=' + filter.organisationId;
      }
      if (filter.campaignId) {
        if (typeof filter.campaignId === 'number' || typeof filter.campaignId === 'string') {
          url += '&campaign=' + encodeURIComponent(String(filter.campaignId));
        } else {
          url += '&campaign=' + (<number[]>filter.campaignId).map(c => encodeURIComponent(String(c))).join(',');
        }
      }
      if (filter.orderStatus === true || filter.orderStatus === false) {
        url += '&orderStatus=' + filter.orderStatus;
      }
      if (filter.completionStatus === true || filter.completionStatus === false) {
        url += '&completionStatus=' + filter.completionStatus;
      }
      if (filter.variety) {
        url += '&variety=' + encodeURIComponent(String(filter.variety));
      }
      if (filter.advisorId) {
        url += '&advisor=' + encodeURIComponent(String(filter.advisorId));
      }
    }

    return url;
  }

  handleParcel(parcel: ARColzaParcel): ARColzaParcel {
    if (parcel.sowedOn) {
      parcel.sowedOn = new Date(<string>parcel.sowedOn);
    }
    parcel.createdAt = new Date(<string>parcel.createdAt);
    parcel.updatedAt = new Date(<string>parcel.updatedAt);
    return parcel;
  }

  getParcel(id: number): Observable<ARColzaParcel> {
    return this.http.get<any>(this.toolUrl + '/parcels/' + id).pipe(
      map((response) => {
        const data: ARColzaParcel = response.data;
        this.handleParcel(data);
        if (data.campaign) {
          this.handleCampaignDates(<ARColzaCampaign>data.campaign);
        }
        if ((<ARColzaCampaign>data.campaign).configuration) {
          this.handleConfigurationDates((<ARColzaCampaign>data.campaign).configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  // FIXME utilisser orderable sur le serveur plutot que conditions completion ou order
  getOrderItems(
    orderItemIds: Array<number>,
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean = null,
  ): Observable<ARColzaOrderItem[]> {
    let url = this.toolUrl + '/orderitems/all';
    url += this.fillOrderItemFilter(orderItemIds, completionStatus, orderStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: ARColzaOrderItem[] = response.data;
        data.forEach((element) => {
          this.handleOrderItemDates(element);
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  countOrderItems(
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean = null,
  ): Observable<number> {
    let url = this.toolUrl + '/orderitems/count';
    url += this.fillOrderItemFilter(null, completionStatus, orderStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: number = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  fillOrderItemFilter(orderItemIds: Array<number>, completionStatus: boolean, orderStatus: boolean) {
    let url = '';
    if (orderItemIds !== null || completionStatus !== null || orderStatus !== null) {
      let q = '';
      if (completionStatus !== null) {
        q += 'completionStatus=' + completionStatus;
      }
      if (orderStatus !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderStatus=' + orderStatus;
      }

      if (orderItemIds !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderItemIds=' + orderItemIds.join(',');
      }
      url += '?' + q;
    }

    return url;
  }

  getOrderItem(id: number): Observable<ARColzaOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/' + id).pipe(
      map((response) => {
        const orderItem: ARColzaOrderItem = response.data;
        return this.handleOrderItemDates(orderItem);
      }),
      catchError(this.handleError),
    );
  }

  getOrderItemByParcel(parcel: Parcel): Observable<ARColzaOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/parcel/' + parcel.id).pipe(
      map((response) => {
        const orderItem: ARColzaOrderItem = response.data;
        return this.handleOrderItemDates(orderItem);
      }),
      catchError(this.handleError),
    );
  }

  saveOrderItem(orderItem: ARColzaOrderItem): Observable<ARColzaOrderItem> {
    if (orderItem.id) {
      return this.putOrderItem(orderItem);
    }
    return this.postOrderItem(orderItem);
  }

  deleteOrderItem(orderItem: ARColzaOrderItem) {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItem(parcel: Parcel) {
    const url = `${this.toolUrl}/orderitems/parcel/${parcel.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItems(parcelIds: number[]): Observable<OrderItemDeletionSummary> {
    const url = `${this.toolUrl}/orderitems/parcel/delete-batch`;
    const body = {
      parcelIds: parcelIds,
    };

    return this.http.post<any>(url, body).pipe(
      map((response) => {
        const parsed = tryJson(response);
        if (parsed && parsed.data) {
          return normalizeDeletionSummary(parsed.data);
        }
        return normalizeDeletionSummary(parsed);
      }),
      catchError(this.handleError),
    );
  }

  // Add new orderItem
  private postOrderItem(config: ARColzaOrderItem): Observable<ARColzaOrderItem> {
    return this.http.post<any>(this.toolUrl + '/orderitems', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrderItem(orderItem: ARColzaOrderItem): Observable<ARColzaOrderItem> {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.put<any>(url, orderItem).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  getPageOrders(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: AROrderFilter,
  ): Observable<Page<ARColzaOrder>> {
    let url = this.toolUrl + '/orders?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    url += fillOrderFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: Page<ARColzaOrder> = response.data;
        for (const order of data.content) {
          this.handleOrderDates(order);
          if (order.campaign) {
            this.handleCampaignDates(order.campaign);
            if (order.campaign.configuration) {
              this.handleConfigurationDates(order.campaign.configuration);
            }
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getOrder(id: number): Observable<ARColzaOrder> {
    return this.http.get<any>(this.toolUrl + '/orders/' + id).pipe(
      map((response) => {
        const data: ARColzaOrder = response.data;
        this.handleOrderDates(data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
          if (data.campaign.configuration) {
            this.handleConfigurationDates(data.campaign.configuration);
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveOrder(order: ARColzaOrder): Observable<ARColzaOrder> {
    if (order.id) {
      return this.putOrder(order);
    }
    return this.postOrder(order);
  }

  deleteOrder(order: ARColzaOrder) {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  getAreaForOrders(filter: AROrderFilter, specy: string): Observable<OrderSummary> {
    let url = `${this.toolUrl}/orders/area?`;
    url += fillOrderFilter(filter);
    if (specy != null) {
      url += `&specy=${encodeURIComponent(specy)}`;
    }
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  exportKml(): Observable<Blob> {
    const url = this.toolUrl + '/kml';
    const applicationType = 'application/zip';

    return this.httpWanaka.blobQuery(url, applicationType);
  }

  // Add new orderItem
  private postOrder(order: ARColzaOrder): Observable<ARColzaOrder> {
    return this.http.post<any>(this.toolUrl + '/orders', order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrder(order: ARColzaOrder): Observable<ARColzaOrder> {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.put<any>(url, order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  protected handleError(error: any) {
    // FIXME
    console.error('An error occurred', error);
    return throwError(() => tryJson(error) || error);
  }

  private handleCampaignDates(campaign: ARColzaCampaign): ARColzaCampaign {
    campaign.startingOn = new Date(<string>campaign.startingOn);
    campaign.endingOn = new Date(<string>campaign.endingOn);
    campaign.createdAt = new Date(<string>campaign.createdAt);
    campaign.updatedAt = new Date(<string>campaign.updatedAt);

    return campaign;
  }

  private handleConfigurationDates(config: ARColzaCampaignConfiguration): ARColzaCampaignConfiguration {
    if (config.startingOn) {
      config.startingOn = new Date(<string>config.startingOn);
    }
    if (config.endingOnEh) {
      config.endingOnEh = new Date(<string>config.endingOnEh);
    }
    if (config.endingOnSh) {
      config.endingOnSh = new Date(<string>config.endingOnSh);
    }

    config.createdAt = new Date(<string>config.createdAt);
    config.updatedAt = new Date(<string>config.updatedAt);

    return config;
  }

  private handleOrderDates(order: ARColzaOrder): ARColzaOrder {
    order.createdAt = new Date(<string>order.createdAt);
    order.updatedAt = new Date(<string>order.updatedAt);

    return order;
  }

  private handleOrderItemDates(orderItem: ARColzaOrderItem): ARColzaOrderItem {
    orderItem.createdAt = new Date(<string>orderItem.createdAt);
    orderItem.updatedAt = new Date(<string>orderItem.updatedAt);

    return orderItem;
  }
}
